import React, { useState, useEffect } from 'react';
import pipelineService from '../services/pipelineService';

/**
 * CardActivity — timeline of all activities on a pipeline card.
 */
const CardActivity = ({ cardId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { loadActivities(); }, [cardId]);

  const loadActivities = async () => {
    try {
      const acts = await pipelineService.getActivities(cardId);
      setActivities(acts);
      setError(null);
    } catch (err) {
      console.error('Error loading activities:', err);
      setError('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type) => {
    const icons = {
      card_created: (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
      ),
      stage_change: (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
      ),
      client_stage_change: (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
      ),
      assignment: (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
      ),
      unassignment: (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a7 7 0 00-7 7h14m1-7l3-3m0 0l3 3m-3-3v6" /></svg>
      ),
      priority_change: (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z" /></svg>
      ),
      comment: (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
      ),
      upload: (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
      ),
      status_change: (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      ),
      document_deleted: (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
      ),
    };
    return icons[type] || (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    );
  };

  const getColor = (type) => {
    const colors = {
      card_created: 'text-green-600 bg-green-100',
      stage_change: 'text-blue-600 bg-blue-100',
      client_stage_change: 'text-indigo-600 bg-indigo-100',
      assignment: 'text-purple-600 bg-purple-100',
      unassignment: 'text-gray-500 bg-gray-100',
      priority_change: 'text-amber-600 bg-amber-100',
      comment: 'text-blue-600 bg-blue-100',
      upload: 'text-cyan-600 bg-cyan-100',
      status_change: 'text-emerald-600 bg-emerald-100',
      document_deleted: 'text-red-500 bg-red-100',
      card_deleted: 'text-red-500 bg-red-100',
    };
    return colors[type] || 'text-gray-500 bg-gray-100';
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatFullDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="py-6 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600 mx-auto"></div></div>;

  return (
    <div className="space-y-3">
      {error && <p className="text-[10px] text-red-500">{error}</p>}

      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-700">Activity</p>
        <button onClick={loadActivities} className="text-[10px] text-gray-400 hover:text-emerald-700 transition-colors" title="Refresh">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
        </button>
      </div>

      {activities.length === 0 ? (
        <div className="text-center py-8">
          <svg className="w-7 h-7 text-gray-300 mx-auto mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-gray-400">No activity yet</p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-gray-200"></div>

          <div className="space-y-0.5">
            {activities.map(activity => (
              <div key={activity.id} className="relative flex items-start gap-2.5 py-1.5 group">
                {/* Icon dot */}
                <div className={`w-[22px] h-[22px] rounded-full flex items-center justify-center flex-shrink-0 z-10 ${getColor(activity.activity_type)}`}>
                  {getIcon(activity.activity_type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pt-0.5">
                  <p className="text-xs text-gray-700 leading-relaxed">{activity.description}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {activity.user_avatar ? (
                      <img src={activity.user_avatar} alt="" className="w-3.5 h-3.5 rounded-full" />
                    ) : (
                      <div className="w-3.5 h-3.5 rounded-full bg-gray-300 flex items-center justify-center text-white text-[7px] font-bold">
                        {activity.user_name?.charAt(0) || '?'}
                      </div>
                    )}
                    <span className="text-[10px] text-gray-400">{activity.user_name || 'System'}</span>
                    <span className="text-[10px] text-gray-300">·</span>
                    <span className="text-[10px] text-gray-400" title={formatFullDate(activity.created_at)}>{formatTime(activity.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activities.length > 0 && (
        <p className="text-[10px] text-gray-300 text-center pt-1">{activities.length} {activities.length === 1 ? 'event' : 'events'}</p>
      )}
    </div>
  );
};

export default CardActivity;
